'use client'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Localization } from '@/payload-types'
import { getLocalizedTranslationData } from '@/utilities/translationsData'

type LocaleContextValue = {
  locale: AppLocale
  translatedData: Localization | null
  localeLoading: boolean
  setLocaleLoading: (val: boolean) => void
  applyLocaleChange: (nextLocale: AppLocale) => void
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  translatedData: null,
  localeLoading: false,
  setLocaleLoading: (_val: boolean) => undefined,
  applyLocaleChange: () => undefined,
})

export const LocaleProvider = ({
  children,
  locale,
  translatedData,
}: {
  children: React.ReactNode
  locale: AppLocale
  translatedData: Localization
}) => {
  const [currentLocale, setCurrentLocale] = useState<AppLocale>(locale)
  const [currentTranslatedData, setCurrentTranslatedData] = useState<Localization | null>(
    translatedData,
  )
  const [localeLoading, setLocaleLoading] = useState(false)

  useEffect(() => {
    const isArabic = currentLocale === 'ar'
    const html = document.documentElement
    const body = document.body

    html.lang = currentLocale
    html.dir = 'ltr'
    html.classList.toggle('font-arabic', isArabic)
    html.classList.toggle('font-gallium', !isArabic)

    body.classList.toggle('font-arabic', isArabic)
    body.classList.toggle('font-gallium', !isArabic)
  }, [currentLocale])

  useEffect(() => {
    setCurrentLocale(locale)
    setCurrentTranslatedData(translatedData)
    setLocaleLoading(false)
  }, [locale, translatedData])

  const applyLocaleChange = useCallback(
    async (nextLocale: AppLocale) => {
      const response = await fetch('/api/localization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: nextLocale }),
      })

      if (!response.ok) {
        toast.error(translatedData?.common.generic_error ?? 'Something went wrong.')
        return
      }

      setCurrentLocale(nextLocale)
      setCurrentTranslatedData(getLocalizedTranslationData(nextLocale) as Localization)
      setLocaleLoading(false)
      window.location.reload()
    },
    [translatedData],
  )

  const value = useMemo(
    () => ({
      locale: currentLocale,
      translatedData: currentTranslatedData,
      localeLoading,
      applyLocaleChange,
      setLocaleLoading,
    }),
    [applyLocaleChange, currentLocale, currentTranslatedData, localeLoading],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export const useLocale = () => useContext(LocaleContext)
